import React, { useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { LoaderCircle, ShieldCheck } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BrandMark } from '../components/BrandMark';
import { useAuth } from '../auth/AuthContext';

function redirectToTarget(target, navigate) {
  const next = String(target || '').trim();
  if (next && (next.startsWith('http://') || next.startsWith('https://') || next.startsWith('/api/'))) {
    // Backend endpoints (like /api/integrations/hkclaim/launch) must bypass SPA router.
    window.location.assign(next);
    return;
  }

  if (next && (next.startsWith('/sso/') || next.startsWith('/signin-') || next.startsWith('/signout-'))) {
    window.location.assign(target);
    return;
  }

  navigate(next || '/tools', { replace: true });
}

export function AuthProgressPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refresh } = useAuth();
  const returnUrl = useMemo(() => searchParams.get('returnUrl') || '/tools', [searchParams]);

  useEffect(() => {
    let isMounted = true;

    refresh()
      .then((user) => {
        if (!isMounted) {
          return;
        }

        if (user) {
          redirectToTarget(returnUrl, navigate);
          return;
        }

        navigate(`/login?authState=signin-failed&returnUrl=${encodeURIComponent(returnUrl)}`, { replace: true });
      })
      .catch(() => {
        if (isMounted) {
          navigate(`/login?authState=signin-failed&returnUrl=${encodeURIComponent(returnUrl)}`, { replace: true });
        }
      });

    return () => {
      isMounted = false;
    };
  }, [navigate, refresh, returnUrl]);

  return (
    <div className="login-screen">
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.25 }}
        className="login-card"
      >
        <BrandMark />
        <div className="login-title-row">
          <div>
            <h2>Completing sign-in</h2>
            <p className="hint-text">We are validating your Kinde session and applying access rules.</p>
          </div>
          <div className="icon-chip">
            <ShieldCheck size={18} />
          </div>
        </div>
        <div className="progress-panel">
          <LoaderCircle size={20} className="spin" />
          <span>Finalizing authentication...</span>
        </div>
      </motion.div>
    </div>
  );
}
